import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api, qs, type Paged } from '../api';
import { useAdmin } from '../auth';
import { Btn, Empty, ErrorBox, Loading, Modal, Pager, fmtDate, useAction } from '../ui';

interface PaymentRow {
  id: string;
  status: 'created' | 'paid' | 'refunded' | 'expired' | 'failed';
  tariffCode: string;
  stars: number;
  listing: { id: string; title: string } | null;
  user: { id: string; name: string; username: string | null };
  createdAt: string;
  paidAt: string | null;
  refundedAt: string | null;
  refundReason: string | null;
  applied: boolean;
}
type PaymentsPage = Paged<PaymentRow> & { totals: Partial<Record<PaymentRow['status'], { count: number; stars: number }>> };

const STATUS: Record<PaymentRow['status'], string> = { created: 'Счёт выставлен', paid: 'Оплачен', refunded: 'Возвращён', expired: 'Не оплачен (истёк)', failed: 'Ошибка' };

/** Покупки продвижения за Telegram Stars: итоги, список, возврат. */
export function PaymentsPage() {
  const { can } = useAdmin();
  const qc = useQueryClient();
  const [status, setStatus] = useState('');
  const [page, setPage] = useState(1);
  const [refunding, setRefunding] = useState<PaymentRow | null>(null);
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['payments', status, page],
    queryFn: () => api<PaymentsPage>(`/admin/payments${qs({ status: status || undefined, page, pageSize: 20 })}`),
  });
  const paid = data?.totals.paid;
  const refunded = data?.totals.refunded;
  return (
    <div>
      <h1>Платежи (Telegram Stars)</h1>
      <p className="muted">
        Продвижение, купленное пользователями в приложении. Вывод Stars — через @BotFather → бот → Stars Balance (Fragment).
        Оплаченный счёт по снятому до оплаты объявлению возвращается автоматически.
      </p>
      {data ? (
        <div className="stats" data-testid="payment-totals">
          <div className="stat"><b>{paid?.stars ?? 0} ⭐</b><span>оплачено ({paid?.count ?? 0})</span></div>
          <div className="stat"><b>{refunded?.stars ?? 0} ⭐</b><span>возвращено ({refunded?.count ?? 0})</span></div>
        </div>
      ) : null}
      <div className="toolbar">
        <select aria-label="Статус" value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }}>
          <option value="">Все</option>
          {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
      </div>
      {isLoading ? <Loading /> : error ? <ErrorBox error={error} onRetry={() => void refetch()} /> : !data?.items.length ? <Empty text="Платежей нет" /> : (
        <>
          <table>
            <thead><tr><th>#</th><th>Дата</th><th>Пользователь</th><th>Объявление</th><th>Тариф</th><th>Stars</th><th>Статус</th><th /></tr></thead>
            <tbody>
              {data.items.map((p) => (
                <tr key={p.id}>
                  <td>{p.id}</td>
                  <td>{fmtDate(p.paidAt ?? p.createdAt)}</td>
                  <td>{p.user.name} {p.user.username ? <span className="muted">@{p.user.username}</span> : null} <span className="muted">#{p.user.id}</span></td>
                  <td>{p.listing ? `#${p.listing.id} ${p.listing.title}` : '—'}</td>
                  <td><code>{p.tariffCode}</code></td>
                  <td>{p.stars}</td>
                  <td>
                    {STATUS[p.status]}
                    {p.status === 'paid' && !p.applied ? <div className="small warn">продвижение не начислено</div> : null}
                    {p.refundReason ? <div className="small muted">{p.refundReason}</div> : null}
                  </td>
                  <td>{p.status === 'paid' && can('payments.refund') ? <Btn kind="secondary" onClick={() => setRefunding(p)}>Вернуть</Btn> : null}</td>
                </tr>
              ))}
            </tbody>
          </table>
          <Pager page={page} pageSize={20} total={data.total} onPage={setPage} />
        </>
      )}
      {refunding ? <RefundDialog payment={refunding} onClose={() => { setRefunding(null); void qc.invalidateQueries({ queryKey: ['payments'] }); }} /> : null}
    </div>
  );
}

function RefundDialog({ payment, onClose }: { payment: PaymentRow; onClose: () => void }) {
  const action = useAction();
  const [reason, setReason] = useState('');
  return (
    <Modal title={`Возврат платежа #${payment.id}`} onClose={onClose}>
      <p>{payment.stars} ⭐ вернутся пользователю {payment.user.name}; продвижение объявления будет отменено.</p>
      <label className="field">Причина (видна в журнале)
        <textarea value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} />
      </label>
      {action.error ? <ErrorBox error={action.error} /> : null}
      <div className="row">
        <Btn busy={action.busy} disabled={reason.trim().length < 3} onClick={() => void action.run(async () => { await api(`/admin/payments/${payment.id}/refund`, { method: 'POST', json: { reason } }); onClose(); })}>
          Вернуть Stars
        </Btn>
        <Btn kind="secondary" onClick={onClose}>Отмена</Btn>
      </div>
    </Modal>
  );
}
